// src/pages/pessoas/ClienteSearch.js
import React, { useEffect, useState } from 'react';
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import { FaSortUp, FaSortDown } from "react-icons/fa";

import LazyLoading from '../../components/modalForm/LazyLoading';
import ModalTable from '../../components/modalForm/ModalTable';

import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalSearchBar from '../../components/modalForm/ModalSearchBar';
import { ModalActionButton } from '../../components/modalForm/ModalActions';
import ModalSelectButton from '../../components/modalForm/ModalSelectButton';

const ClienteSearch = ({ show, onHide, onSelectCliente }) => {
    const { clientes } = useCliente();
    const [filter, setFilter] = useState('');
    const [sortField, setSortField] = useState('id'); // 'id' ou 'nome'
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ou 'desc'

    useEffect(() => {
        // Limpa campos quando o modal é aberto
        if (show) {
            setFilter('');
            // Reseta para a primeira página ao abrir o modal: não é mais necessário com a rolagem
        }
    }, [show]);

    if (!show) return null;

    const normalizeString = (str) => {
        return str
            ?.normalize("NFD") // Normaliza para decompor caracteres acentuados
            .replace(/[\u0300-\u036f]/g, "") // Remove os diacríticos
            .toLowerCase(); // Converte para minúsculo
    };

    // Filtra os clientes pelo nome
    const filteredClientes = clientes.filter(cliente =>
        normalizeString(cliente.pessoa?.nome).includes(normalizeString(filter))
    );

    // Ordena os clientes de acordo com o campo e direção selecionados
    const sortedClientes = filteredClientes.sort((a, b) => {
        if (sortField === 'id') {
            return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
        } else if (sortField === 'nome') {
            const nomeA = a.pessoa?.nome.toLowerCase();
            const nomeB = b.pessoa?.nome.toLowerCase();
            if (nomeA < nomeB) return sortDirection === 'asc' ? -1 : 1;
            if (nomeA > nomeB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        }
        return 0; // Retorno padrão
    });

    // Divide os clientes em páginas: comportamento substituído pela rolagem da tabela
    // Número total de páginas: não é mais necessário porque todos os resultados são exibidos

    // Função para alternar a ordenação por campo
    const handleSort = (field) => {
        if (sortField === field) {
            // Se já estiver ordenando pelo mesmo campo, inverte a direção
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc'); // Reseta para 'asc' ao mudar de campo
        }
    };

    const handleClose = () => {
        setFilter('');
        onHide();
    };

    // Função para lidar com o clique duplo na linha da tabela
    const handleDoubleClick = (cliente) => {
        onSelectCliente(cliente); // Passa o cliente para o método de seleção
        handleClose(); // Fecha o modal
    };

    return (
        <ModalContainer show={show} largura="600px" variant="busca" onHide={handleClose}>

            <ModalHeader title="Buscar Cliente" onHide={handleClose} />
            <ModalBody className="modal-body">
                <div className="mb-3">
                    <ModalSearchBar
                        type="text"

                        placeholder="Filtrar por nome..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        autoFocus
                    />
                </div>

                <LazyLoading
                    items={sortedClientes}
                    resetKey={`${filter}|${sortField}|${sortDirection}`}
                >
                {({ containerRef, handleScroll, visibleItems }) => (
                <ModalTable
                    containerRef={containerRef}
                    containerStyle={{ maxHeight: '55vh', overflowY: 'auto' }}
                    onContainerScroll={handleScroll}
                >
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                            <th className="text-center align-middle" scope="col" style={{ width: '10%' }} aria-sort={sortField === 'id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button onClick={() => handleSort('id')}
                                    style={{ fontWeight:'bold', background: 'none', border: 'none' }}
                                    tabIndex={0}
                                 type="button">
                                    Id
                                    {sortField === 'id' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                                </button>
                            </th>
                            <th className="text-start align-middle" scope="col" style={{ width: '60%' }} aria-sort={sortField === 'nome' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button onClick={() => handleSort('nome')}
                                    style={{ fontWeight:'bold', background: 'none', border: 'none' }}
                                    tabIndex={0}
                                 type="button">
                                    Nome
                                    {sortField === 'nome' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                                </button>
                            </th>
                            <th className="text-center align-middle modal-select-column" scope="col" style={{ width: '20%' }}>Selecionar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map(cliente => (
                            <tr key={cliente.id}
                            onDoubleClick={() => handleDoubleClick(cliente)} // Manipulador de duplo clique
                            >
                                <td className="text-end align-middle">{cliente.id}</td>
                                <td className="text-start align-middle">{cliente.pessoa?.nome}</td>
                                <td className="text-center align-middle modal-select-column">
                                <ModalSelectButton className="btn btn-outline-secondary btn-sm" onClick={() => onSelectCliente(cliente)} style={{ padding: '2px 6px' }} label={cliente.pessoa?.nome}>{/* Cor cinza escuro */}</ModalSelectButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </ModalTable>
                )}
                </LazyLoading>
            </ModalBody>
            <ModalFooter className="modal-footer">
                {/* Paginação removida para utilizar a rolagem da tabela */}

                <ModalActionButton type="button" className="btn btn-secondary btn-sm" onClick={handleClose} action="close">
                    Fechar
                </ModalActionButton>
            </ModalFooter>

        </ModalContainer>
    );
};

export default ClienteSearch;
